import { useCallback, useMemo } from 'react';

import { useApolloClient } from '@apollo/client';
import { getLoyaltyServiceServiceMode } from 'features/offer/utils/service-mode';

import { usePersonalizedPromotionsExperiment } from 'experiments/personalized-promotions/use-personalized-promotions-experiment';
import {
  LoyaltyServiceBestPromotionDocument,
  useLoyaltyServiceBestPromotionQuery,
} from 'generated/graphql-gateway';
import { useServiceModeLive } from 'state/service-mode';

import { BestPromotionResult, BestPromotionSourceArgs, shouldClearActivateParam } from '../types';
import {
  buildBestPromotionInput,
  mapAdapterEntriesToServiceEntries,
} from '../utils/map-best-promotion-input';

export function useLoyaltyServiceBestPromotion({
  enabled,
  cartEntries,
  storeId,
  skipStoreDayPartHours,
  onActivateResolved,
  readMode = 'live',
}: BestPromotionSourceArgs): BestPromotionResult {
  const serviceMode = getLoyaltyServiceServiceMode(useServiceModeLive());
  const apolloClient = useApolloClient();
  const {
    collectionId,
    getExposureCandidateForResult,
    isAssignmentPending,
  } = usePersonalizedPromotionsExperiment(enabled);

  const input = useMemo(
    () =>
      buildBestPromotionInput({
        cartEntries: cartEntries?.length
          ? mapAdapterEntriesToServiceEntries(cartEntries)
          : undefined,
        collectionId,
        serviceMode,
        storeId,
        skipStoreDayPartHours,
      }),
    [cartEntries, collectionId, serviceMode, storeId, skipStoreDayPartHours]
  );

  const { data } = useLoyaltyServiceBestPromotionQuery({
    variables: { input },
    skip: !enabled || isAssignmentPending,
    fetchPolicy: readMode === 'cache-only' ? 'cache-only' : 'cache-and-network',
    onCompleted: completed => {
      const completedPromotion = completed?.loyaltyServiceBestPromotion;
      if (shouldClearActivateParam(completedPromotion)) {
        onActivateResolved?.();
      }
    },
  });

  const refetchPromotion = useCallback(async () => {
    await apolloClient.refetchQueries({ include: [LoyaltyServiceBestPromotionDocument] });
    const rereadPromotion = apolloClient.readQuery({
      query: LoyaltyServiceBestPromotionDocument,
      variables: { input },
    })?.loyaltyServiceBestPromotion;
    return {
      promotionId: rereadPromotion?.id,
      mustActivate: rereadPromotion?.mustActivate,
      userActivatedAt: rereadPromotion?.userActivatedAt,
    };
  }, [apolloClient, input]);

  // Apollo can retain the previous variables' data when a query becomes skipped.
  // Hide that snapshot while the current identity's assignment is unresolved.
  const promotion = isAssignmentPending ? undefined : data?.loyaltyServiceBestPromotion;
  const cms = promotion?.cmsContent;
  const promotionExposure = useMemo(
    () => getExposureCandidateForResult(data?.loyaltyServiceBestPromotion),
    [data, getExposureCandidateForResult]
  );

  return {
    promotionId: promotion?.id,
    mustActivate: promotion?.mustActivate,
    userActivatedAt: promotion?.userActivatedAt,
    content: cms
      ? {
          name: cms.name,
          loyaltyEngineId: cms.loyaltyEngineId,
          promoMessage: cms.promoMessage,
          promoMessageBlock: cms.promoMessageBlock?.locale,
          activatedPromoMessageBlock: cms.activatedPromoMessageBlock?.locale,
        }
      : null,
    promotionExposure,
    refetchPromotion,
  };
}
